/**
 * Maestro Verified device catalog — cache-first fetch from cloud API.
 */

import { CLOUD_API_URL } from '../config/serverConfig';
import {
  normalizeVerifiedPayload,
  type VerifiedDeviceEntry,
} from '../utils/verifiedDevicesUtils';

const CACHE_KEY = 'maestro_verified_devices_cache';
const VERIFIED_PATH = '/api/devices/verified';
const CERTIFIED_FALLBACK_PATH = '/api/devices/certified';

export interface VerifiedDevicesCatalog {
  devices: VerifiedDeviceEntry[];
  count: number;
  fetchedAt: number;
  source: 'network' | 'cache';
  stale: boolean;
}

interface CacheBlob {
  devices: VerifiedDeviceEntry[];
  fetchedAt: number;
}

let memoryCache: CacheBlob | null = null;
let inflight: Promise<VerifiedDevicesCatalog> | null = null;

function readCache(): CacheBlob | null {
  if (memoryCache) return memoryCache;
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as CacheBlob;
    if (!parsed || !Array.isArray(parsed.devices) || typeof parsed.fetchedAt !== 'number') return null;
    memoryCache = parsed;
    return parsed;
  } catch {
    return null;
  }
}

function writeCache(devices: VerifiedDeviceEntry[]): CacheBlob {
  const blob: CacheBlob = { devices, fetchedAt: Date.now() };
  memoryCache = blob;
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify(blob));
  } catch {
    /* ignore quota */
  }
  return blob;
}

function toCatalog(
  devices: VerifiedDeviceEntry[],
  fetchedAt: number,
  source: 'network' | 'cache',
  stale: boolean,
): VerifiedDevicesCatalog {
  return {
    devices,
    count: devices.length,
    fetchedAt,
    source,
    stale,
  };
}

async function fetchFromNetwork(): Promise<VerifiedDeviceEntry[]> {
  const tryPath = async (path: string) => {
    const res = await fetch(`${CLOUD_API_URL}${path}`, {
      method: 'GET',
      headers: { Accept: 'application/json' },
    });
    if (!res.ok) return null;
    const json = await res.json();
    return normalizeVerifiedPayload(json);
  };

  const verified = await tryPath(VERIFIED_PATH);
  if (verified && verified.length > 0) return verified;

  const certified = await tryPath(CERTIFIED_FALLBACK_PATH);
  if (certified && certified.length > 0) return certified;

  if (verified) return verified;
  if (certified) return certified;
  throw new Error('Verified devices API unavailable');
}

export function getCachedVerifiedDevices(): VerifiedDevicesCatalog {
  const cached = readCache();
  if (cached) {
    return toCatalog(cached.devices, cached.fetchedAt, 'cache', false);
  }
  return toCatalog([], 0, 'cache', false);
}

/** Cache-first: returns immediately, refreshes in background when possible. */
export function loadVerifiedDevices(options?: { force?: boolean }): Promise<VerifiedDevicesCatalog> {
  if (!options?.force && inflight) return inflight;

  const cached = readCache();
  const baseline = cached
    ? toCatalog(cached.devices, cached.fetchedAt, 'cache', false)
    : toCatalog([], 0, 'cache', false);

  inflight = (async () => {
    try {
      const devices = await fetchFromNetwork();
      const blob = writeCache(devices);
      return toCatalog(blob.devices, blob.fetchedAt, 'network', false);
    } catch {
      if (cached) {
        return toCatalog(cached.devices, cached.fetchedAt, 'cache', true);
      }
      return baseline;
    } finally {
      inflight = null;
    }
  })();

  if (cached && !options?.force) {
    void inflight;
    return Promise.resolve(baseline);
  }

  return inflight;
}

export function subscribeVerifiedDevices(
  onUpdate: (catalog: VerifiedDevicesCatalog) => void,
): () => void {
  onUpdate(getCachedVerifiedDevices());
  let cancelled = false;

  void loadVerifiedDevices().then((catalog) => {
    if (!cancelled) onUpdate(catalog);
  });

  return () => { cancelled = true; };
}
